import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ScoresPrintView from './ScoresPrintView';
import AddBowlerModalAdmin from './AddBowlerModalAdmin';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Match {
  id: number;
  squadId: number;
  squadName: string;
  squadDate: string;
  team1Id: number;
  team2Id: number;
  team1Name: string;
  team2Name: string;
  scheduledTime: string;
  result?: string;
  team1Lane?: number;
  team2Lane?: number;
  customFormula?: string;
}

interface TeamMember {
  id: number;
  teamId: number;
  playerId: number;
  playerName: string;
  average: number;
  handicap: number;
  setAverage: number;
  sortOrder: number;
}

interface MatchScoringProps {
  matchId: number;
  leagueId: number;
  leagueName?: string;
  onBack: () => void;
}

const MatchScoring: React.FC<MatchScoringProps> = ({ matchId, leagueId, leagueName, onBack }) => {
  const { t } = useTranslation();
  const [match, setMatch] = useState<Match | null>(null);
  const [team1Members, setTeam1Members] = useState<TeamMember[]>([]);
  const [team2Members, setTeam2Members] = useState<TeamMember[]>([]);
  const [gamesCount, setGamesCount] = useState(3);
  const [scores, setScores] = useState<Record<string, number | undefined>>({});
  const [scoresHandicap, setScoresHandicap] = useState<Record<string, number | undefined>>({});
  const [bakerGames, setBakerGames] = useState<number[]>([]); // Array of game numbers that are baker games
  const [bakerScores, setBakerScores] = useState<Record<string, number | undefined>>({}); // Format: "team1-gameNum" or "team2-gameNum"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // DISABLED: Editing handicap and average - can be re-enabled in the future
  // const [editingHandicap, setEditingHandicap] = useState<number | null>(null);
  // const [editingAverage, setEditingAverage] = useState<number | null>(null);
  const [draggedPlayer, setDraggedPlayer] = useState<TeamMember | null>(null);
  const [savingScores, setSavingScores] = useState(false);
  const [scoresSaved, setScoresSaved] = useState(false);
  const [matchResults, setMatchResults] = useState<{
    team1SeriesTotal: number;
    team2SeriesTotal: number;
    team1SeriesHandicap: number;
    team2SeriesHandicap: number;
    team1Points: number;
    team2Points: number;
    gameWinners: number[];
    seriesWinner: number;
    seriesWinnerHandicap: number;
  } | null>(null);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [scoresModified, setScoresModified] = useState(false);
  const [unpaidMemberIds, setUnpaidMemberIds] = useState<Set<number>>(new Set());
  
  // Extra points state
  const [team1ExtraPoints, setTeam1ExtraPoints] = useState<string>('');
  const [team2ExtraPoints, setTeam2ExtraPoints] = useState<string>('');
  const [team1RemovePoints, setTeam1RemovePoints] = useState<string>('');
  const [team2RemovePoints, setTeam2RemovePoints] = useState<string>('');
  const [adjustingPoints, setAdjustingPoints] = useState(false);
  const [team1PointsSuccess, setTeam1PointsSuccess] = useState('');
  const [team2PointsSuccess, setTeam2PointsSuccess] = useState('');

  // Backup bowlers state
  const [backupBowlers, setBackupBowlers] = useState<Record<string, {
    backupBowlerId: number;
    backupBowlerName: string;
    average: number;
    handicap: number;
  }>>({});
  const [showBackupSelector, setShowBackupSelector] = useState<{playerId: number, playerName: string} | null>(null);
  const [availableBowlers, setAvailableBowlers] = useState<any[]>([]);
  const [bowlerSearchTerm, setBowlerSearchTerm] = useState('');
  const [loadingBowlers, setLoadingBowlers] = useState(false);
  const [bowlerFetchError, setBowlerFetchError] = useState<string | null>(null);
  const [showAddBowlerInBackup, setShowAddBowlerInBackup] = useState(false);
  
  // Blind configuration state
  const [blindConfig, setBlindConfig] = useState<{
    botBlindAverage: number | null;
    bowlerBlindPercentage: number | null;
    bowlerBlindIncludeHandicap: boolean;
  } | null>(null);

  // Handicap config for team-difference calculation
  const [handicapConfig, setHandicapConfig] = useState<{
    handicap_type: 'individual' | 'team_difference';
    team_difference_percentage: number | null;
  }>({ handicap_type: 'individual', team_difference_percentage: null });
  
  // Loaded team game totals (from database with backup handicaps baked in)
  const [loadedTeamGameTotals, setLoadedTeamGameTotals] = useState<Record<string, {
    team1Total: number;
    team2Total: number;
    team1Scratch: number;
    team2Scratch: number;
  }>>({});
  
  // Fetch available bowlers for backup selection
  const fetchAvailableBowlers = async (searchTerm: string) => {
    setLoadingBowlers(true);
    setBowlerFetchError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/leagues/${leagueId}/bowlers?search=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableBowlers(data.bowlers || []);
      } else {
        const data = await response.json().catch(() => ({}));
        setBowlerFetchError(data.error || `Failed to load bowlers (${response.status})`);
        setAvailableBowlers([]);
      }
    } catch (error) {
      console.error('Error fetching bowlers:', error);
      setBowlerFetchError('Network error — could not load bowlers');
      setAvailableBowlers([]);
    } finally {
      setLoadingBowlers(false);
    }
  };

  // Handle selecting a backup bowler
  const handleSelectBackup = (bowler: any, absentPlayerId: number) => {
    // Store backup info for all games for this player
    const updatedBackups = { ...backupBowlers };
    
    for (let game = 1; game <= gamesCount; game++) {
      if (!bakerGames.includes(game)) {
        const key = `${absentPlayerId}-${game}`;
        updatedBackups[key] = {
          backupBowlerId: bowler.id,
          backupBowlerName: bowler.name,
          average: bowler.average || 0,
          handicap: bowler.handicap || 0
        };
      }
    }
    
    setBackupBowlers(updatedBackups);
    setShowBackupSelector(null);
    setBowlerSearchTerm('');
    setBowlerFetchError(null);
    setShowAddBowlerInBackup(false);
    
    // Mark scores as modified if they were already saved
    if (scoresSaved) {
      setScoresModified(true);
    }
    
    // Clear loaded handicap totals since backup changed - will be recalculated on save
    setLoadedTeamGameTotals({});
  };

  // Handle updating backup bowler handicap
  const handleUpdateBackupHandicap = (playerId: number, newHandicap: number) => {
    const updatedBackups = { ...backupBowlers };
    let hasUpdate = false;
    
    // Update handicap for ALL games for this player
    for (let game = 1; game <= gamesCount; game++) {
      const key = `${playerId}-${game}`;
      if (updatedBackups[key]) {
        updatedBackups[key] = {
          ...updatedBackups[key],
          handicap: newHandicap
        };
        hasUpdate = true;
      }
    }
    
    if (hasUpdate) {
      setBackupBowlers(updatedBackups);
      setScoresModified(true);
      // Clear loaded handicap totals since backup handicap changed - will be recalculated on save
      setLoadedTeamGameTotals({});
    }
  };

  // Handle removing a backup bowler
  const handleRemoveBackup = (playerId: number) => {
    const updatedBackups = { ...backupBowlers };
    
    // Remove backup info for all games for this player
    for (let game = 1; game <= gamesCount; game++) {
      const key = `${playerId}-${game}`;
      delete updatedBackups[key];
    }
    
    setBackupBowlers(updatedBackups);
    
    // Mark scores as modified if they were already saved
    if (scoresSaved) {
      setScoresModified(true);
    }
    
    // Clear loaded handicap totals since backup removed - will be recalculated on save
    setLoadedTeamGameTotals({});
  };

  // Handle using blind for absent bowler
  const handleUseBlind = (member: TeamMember) => {
    if (!blindConfig) {
      alert(t('matchScoring.blindConfigNotFound'));
      return;
    }

    // Calculate blind score based on configuration
    const bowlerAverage = member.average;
    const bowlerHandicap = member.handicap;
    
    // Bowler blind percentage (default 100% if not set)
    const percentage = blindConfig.bowlerBlindPercentage || 100;
    let blindScore = Math.round((bowlerAverage * percentage) / 100);
    
    // Add handicap if configured
    if (blindConfig.bowlerBlindIncludeHandicap) {
      blindScore += bowlerHandicap;
    }
    
    // Cap at 300
    blindScore = Math.min(blindScore, 300);
    
    // Set blind score for ALL regular games (not baker games)
    const updatedScores = { ...scores };
    for (let game = 1; game <= gamesCount; game++) {
      if (!bakerGames.includes(game)) {
        const scoreKey = `${member.playerId}-${game}`;
        updatedScores[scoreKey] = blindScore;
      }
    }
    
    // Add backup entry with bowler_id = 1 (blind marker)
    const updatedBackups = { ...backupBowlers };
    for (let game = 1; game <= gamesCount; game++) {
      if (!bakerGames.includes(game)) {
        const key = `${member.playerId}-${game}`;
        updatedBackups[key] = {
          backupBowlerId: 1, // Special ID for blind
          backupBowlerName: t('matchScoring.blind'),
          average: blindScore,
          handicap: blindConfig.bowlerBlindIncludeHandicap ? bowlerHandicap : 0
        };
      }
    }
    
    setScores(updatedScores);
    setBackupBowlers(updatedBackups);
    setScoresModified(true);
    
    // Clear loaded handicap totals since blind scores applied - will be recalculated on save
    setLoadedTeamGameTotals({});
  };

  // Check if a player has a backup assigned
  const hasBackup = (playerId: number) => {
    for (let game = 1; game <= gamesCount; game++) {
      const key = `${playerId}-${game}`;
      if (backupBowlers[key]) return true;
    }
    return false;
  };

  // Get backup info for a player
  const getBackupInfo = (playerId: number) => {
    for (let game = 1; game <= gamesCount; game++) {
      const key = `${playerId}-${game}`;
      if (backupBowlers[key]) return backupBowlers[key];
    }
    return null;
  };

  // Fetch match details and team members
  const fetchMatchData = async () => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // Fetch match details
      const matchResponse = await fetch(`${apiUrl}/admin/scoring/matches/${matchId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!matchResponse.ok) {
        const errorData = await matchResponse.json();
        setError(errorData.error || t('matchScoring.errors.failedToFetchMatch'));
        return;
      }

      const matchData = await matchResponse.json();
      setMatch(matchData);

      // Fetch blind configuration for this league
      try {
        const blindConfigResponse = await fetch(`${apiUrl}/admin/leagues/${leagueId}/handicap-config`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (blindConfigResponse.ok) {
          const config = await blindConfigResponse.json();
          setBlindConfig({
            botBlindAverage: config.bot_blind_average,
            bowlerBlindPercentage: config.bowler_blind_percentage,
            bowlerBlindIncludeHandicap: config.bowler_blind_include_handicap || false
          });
          setHandicapConfig({
            handicap_type: config.handicap_type || 'individual',
            team_difference_percentage: config.team_difference_percentage ?? null
          });
        }
      } catch (error) {
        console.error('Error fetching blind configuration:', error);
      }

      // Fetch games count for this squad
      const gamesResponse = await fetch(`${apiUrl}/schedule/${leagueId}/games-count/${matchData.squadId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (gamesResponse.ok) {
        const { gamesCount: count } = await gamesResponse.json();
        setGamesCount(count || 3);
      }

      // Fetch games details to detect baker games
      const gamesDetailsResponse = await fetch(`${apiUrl}/admin/scoring/match/${matchId}/games`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (gamesDetailsResponse.ok) {
        const gamesData = await gamesDetailsResponse.json();
        if (gamesData.games) {
          const bakerGameNumbers = gamesData.games
            .filter((game: any) => game.baker === 1)
            .map((game: any) => game.game_number);
          setBakerGames(bakerGameNumbers);
        }
      }

      // Fetch team members for both teams
      // Skip team2 if it's Bot Blind (team2Id === 0)
      const isBotBlindMatch = matchData.team2Id === 0;
      
      const team1Response = await fetch(`${apiUrl}/admin/scoring/teams/${matchData.team1Id}/members`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      let team2Response = null;
      if (!isBotBlindMatch) {
        team2Response = await fetch(`${apiUrl}/admin/scoring/teams/${matchData.team2Id}/members`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      }

      if (team1Response.ok && (isBotBlindMatch || (team2Response && team2Response.ok))) {
        const team1Data = await team1Response.json();
        const team2Data = isBotBlindMatch ? { members: [] } : await team2Response!.json();

        setTeam1Members(team1Data.members || []);
        setTeam2Members(team2Data.members || []);
        
        // Pre-fill Bot Blind scores if this is a Bot Blind match
        if (isBotBlindMatch && blindConfig?.botBlindAverage) {
          const botBlindScores: Record<string, number> = {};
          const botPlayerId = 999999; // Virtual player ID for Bot Blind
          
          for (let game = 1; game <= gamesCount; game++) {
            if (!bakerGames.includes(game)) {
              const key = `${botPlayerId}-${game}`;
              botBlindScores[key] = blindConfig.botBlindAverage;
            }
          }
          
          // Only set if scores haven't been saved yet
          setScores(prev => ({ ...prev, ...botBlindScores }));
        }

        // Check for existing scores after team members are loaded
        try {
          const scoresResponse = await fetch(`${apiUrl}/admin/scoring/match/${matchId}/scores`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });

          if (scoresResponse.ok) {
            const scoresData = await scoresResponse.json();
            if (scoresData.hasScores) {
              setScores(scoresData.scores || {});
              setScoresHandicap(scoresData.scoresHandicap || {});
              
              // Load baker scores if they exist
              if (scoresData.bakerScores) {
                setBakerScores(scoresData.bakerScores);
              }

              // Load backup bowler information if it exists
              if (scoresData.backupInfo) {
                const backupBowlersData: Record<string, any> = {};
                Object.entries(scoresData.backupInfo).forEach(([key, info]: [string, any]) => {
                  backupBowlersData[key] = {
                    backupBowlerId: info.backupBowlerId,
                    backupBowlerName: info.backupBowlerName,
                    average: 0, // Will be fetched if needed
                    handicap: 0 // Will be fetched if needed
                  };
                });
                setBackupBowlers(backupBowlersData);
              }
              
              // Load pre-calculated team game totals (with backup handicaps baked in)
              if (scoresData.teamGameTotals) {
                setLoadedTeamGameTotals(scoresData.teamGameTotals);
              }
              
              setScoresSaved(true);

              // Recalculate winners based on loaded scores and pre-calculated team game totals
              const recalculatedResults = recalculateWinners(
                scoresData.scores || {},
                team1Data.members || [],
                team2Data.members || [],
                scoresData.teamGameTotals || {} // Pass loaded team game totals with backup handicaps
              );

              if (recalculatedResults && scoresData.matchResults) {
                setMatchResults({
                  ...scoresData.matchResults,
                  ...recalculatedResults
                });
              }
            }
          }
        } catch (error) {
          console.error('Error loading existing scores:', error);
          // Don't fail the whole component if scores can't be loaded
        }
      }

      // Fetch payment status for this squad (informational, non-blocking)
      if (matchData.squadId) {
        try {
          const psRes = await fetch(`${apiUrl}/admin/quick-scores/squads/${matchData.squadId}/payment-status`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (psRes.ok) {
            const psData = await psRes.json();
            setUnpaidMemberIds(new Set((psData.unpaidMembers || []).map((m: any) => m.bowlerId)));
          }
        } catch { /* non-blocking */ }
      }

    } catch (error) {
      console.error('Fetch match data error:', error);
      setError(t('matchScoring.errors.failedToFetchMatch'));
    } finally {
      setLoading(false);
    }
  };

  // Calculate series total for a player
  const calculatePlayerSeries = (playerId: number, withHandicap: boolean = false) => {
    let total = 0;
    let gamesPlayed = 0;

    for (let game = 1; game <= gamesCount; game++) {
      // Skip baker games for individual player series (baker games are team-only)
      if (bakerGames.includes(game)) continue;
      
      const scoreKey = `${playerId}-${game}`;
      
      if (withHandicap && scoresSaved && scoresHandicap[scoreKey] !== undefined) {
        // Use pre-calculated score with handicap from database (includes backup handicap)
        total += scoresHandicap[scoreKey]!;
        gamesPlayed++;
      } else {
        const score = scores[scoreKey];
        if (score !== undefined && score !== null && score >= 0) {
          total += score;
          gamesPlayed++;

          if (withHandicap && handicapConfig.handicap_type !== 'team_difference') {
            // Fallback: Calculate with current individual handicap
            // For team_difference leagues, handicap is at team level, not per-player
            const backupKey = `${playerId}-${game}`;
            if (backupBowlers[backupKey]) {
              total += backupBowlers[backupKey].handicap;
            } else {
              const player = [...team1Members, ...team2Members].find(p => p.playerId === playerId);
              if (player) total += player.handicap;
            }
          }
        }
      }
    }

    return { total, gamesPlayed };
  };

  // Calculate team-difference handicap (0 for individual-handicap leagues)
  const getTeamDiffHandicap = (): { teamHandicap: number; lowerTeamId: number | null } => {
    if (handicapConfig.handicap_type !== 'team_difference' || !handicapConfig.team_difference_percentage) {
      return { teamHandicap: 0, lowerTeamId: null };
    }
    // Use set_average (from bowlerleaguestats) for team difference calculation
    // Use calc avg (league_average) when available (week 2+), fall back to set avg for week 1
    const t1AvgSum = team1Members.reduce((s, m) => {
      const eff = Number(m.average) > 0 ? Number(m.average) : Number(m.setAverage);
      return s + (eff || 0);
    }, 0);
    const t2AvgSum = team2Members.reduce((s, m) => {
      const eff = Number(m.average) > 0 ? Number(m.average) : Number(m.setAverage);
      return s + (eff || 0);
    }, 0);
    const diff = Math.abs(t1AvgSum - t2AvgSum);
    const teamHandicap = Math.floor(diff * handicapConfig.team_difference_percentage / 100);
    const lowerTeamId = t1AvgSum < t2AvgSum ? (match?.team1Id ?? null) : t2AvgSum < t1AvgSum ? (match?.team2Id ?? null) : null;
    return { teamHandicap, lowerTeamId };
  };

  // Calculate team total for a specific game
  const calculateTeamGameTotal = (teamMembers: TeamMember[], gameNumber: number, withHandicap: boolean = false) => {
    // Check if this is a baker game
    if (bakerGames.includes(gameNumber)) {
      // For baker games, get the team score from baker scores
      const teamId = teamMembers[0]?.teamId;
      const bakerKey = `team${teamId === match?.team1Id ? '1' : '2'}-${gameNumber}`;
      const bakerScore = bakerScores[bakerKey];
      
      if (bakerScore !== undefined && bakerScore !== null && bakerScore >= 0) {
        return { total: bakerScore, playersWithScores: teamMembers.length };
      }
      return { total: 0, playersWithScores: 0 };
    }
    
    // If we have loaded totals from database and scores are saved, use them (scratch or handicap)
    if (loadedTeamGameTotals[gameNumber] && scoresSaved) {
      const teamId = teamMembers[0]?.teamId;
      const isTeam1 = teamId === match?.team1Id;
      
      // Get appropriate total from database (scratch or handicap)
      const savedTotal = withHandicap
        ? (isTeam1 ? loadedTeamGameTotals[gameNumber].team1Total : loadedTeamGameTotals[gameNumber].team2Total)
        : (isTeam1 ? loadedTeamGameTotals[gameNumber].team1Scratch : loadedTeamGameTotals[gameNumber].team2Scratch);
      
      // Count how many players have scores to return accurate playersWithScores
      let playersWithScores = 0;
      teamMembers.forEach(member => {
        const scoreKey = `${member.playerId}-${gameNumber}`;
        const score = scores[scoreKey];
        if (score !== undefined && score !== null && score >= 0) {
          playersWithScores++;
        }
      });
      
      return { total: savedTotal, playersWithScores };
    }
    
    // Regular game - sum individual bowler scores
    let total = 0;
    let playersWithScores = 0;

    teamMembers.forEach(member => {
      const scoreKey = `${member.playerId}-${gameNumber}`;
      const score = scores[scoreKey];
      if (score !== undefined && score !== null && score >= 0) {
        total += score;
        playersWithScores++;

        if (withHandicap && handicapConfig.handicap_type !== 'team_difference') {
          // Individual handicap: add per-member handicap
          const backupKey = `${member.playerId}-${gameNumber}`;
          const handicapToUse = backupBowlers[backupKey] ? backupBowlers[backupKey].handicap : member.handicap;
          total += handicapToUse;
        }
      }
    });

    // Team-difference handicap: add team handicap to the lower-avg team
    if (withHandicap && handicapConfig.handicap_type === 'team_difference' && playersWithScores > 0) {
      const { teamHandicap, lowerTeamId } = getTeamDiffHandicap();
      const thisTeamId = teamMembers[0]?.teamId;
      if (thisTeamId === lowerTeamId && teamHandicap > 0) {
        total += teamHandicap;
      }
    }

    return { total, playersWithScores };
  };

  // Calculate team series total
  const calculateTeamSeries = (teamMembers: TeamMember[], withHandicap: boolean = false) => {
    let total = 0;

    for (let game = 1; game <= gamesCount; game++) {
      const gameTotal = calculateTeamGameTotal(teamMembers, game, withHandicap);
      total += gameTotal.total;
    }

    return total;
  };

  // Determine game winners and series winner (if scores are saved)
  const getGameWinner = (gameNumber: number) => {
    if (!matchResults) return null;
    const gameIndex = gameNumber - 1;
    return matchResults.gameWinners[gameIndex];
  };

  const getSeriesWinner = () => {
    if (!matchResults) return null;
    return matchResults.seriesWinner;
  };

  const getSeriesWinnerHandicap = () => {
    if (!matchResults) return null;
    return matchResults.seriesWinnerHandicap;
  };

  // Recalculate winners based on loaded scores and pre-calculated team game totals
  const recalculateWinners = (
    loadedScores: Record<string, number>, 
    team1: TeamMember[], 
    team2: TeamMember[],
    loadedTeamTotals?: Record<string, { team1Total: number; team2Total: number }>
  ) => {
    if (!loadedScores || Object.keys(loadedScores).length === 0) return null;

    const gameWinners: number[] = [];
    let team1SeriesTotal = 0;
    let team2SeriesTotal = 0;
    let team1SeriesHandicap = 0;
    let team2SeriesHandicap = 0;

    // Check if this is a Bot Blind match (team2 is empty or team2Id is 0)
    const isBotBlindMatch = team2.length === 0 || match?.team2Id === 0;
    const botBlindAverage = blindConfig?.botBlindAverage || 0;

    // Calculate total games including baker games
    const maxBakerGame = bakerGames.length > 0 ? Math.max(...bakerGames) : 0;
    const totalGames = Math.max(gamesCount, maxBakerGame);

    // Calculate game winners and totals
    for (let game = 1; game <= totalGames; game++) {
      // Check if this game is a baker game
      if (bakerGames.includes(game)) {
        // For baker games, use team scores directly
        const team1BakerScore = bakerScores[`team1-${game}`] || 0;
        const team2BakerScore = isBotBlindMatch ? botBlindAverage : (bakerScores[`team2-${game}`] || 0);

        team1SeriesTotal += team1BakerScore;
        team2SeriesTotal += team2BakerScore;
        team1SeriesHandicap += team1BakerScore; // Baker games typically don't use handicap
        team2SeriesHandicap += team2BakerScore; // Bot Blind has no handicap

        // Determine baker game winner
        if (team1BakerScore > team2BakerScore) {
          gameWinners.push(1);
        } else if (team2BakerScore > team1BakerScore) {
          gameWinners.push(2);
        } else {
          gameWinners.push(0); // Tie
        }
      } else {
        // Regular game
        let team1GameTotal = 0;
        let team2GameTotal = 0;
        let team1GameHandicap = 0;
        let team2GameHandicap = 0;

        // Calculate team1 scratch totals
        team1.forEach(member => {
          const score = loadedScores[`${member.playerId}-${game}`] || 0;
          team1GameTotal += score;
        });

        // Calculate team2 scratch totals (or use Bot Blind average)
        if (isBotBlindMatch) {
          // Bot Blind: each "player" scores the blind average
          // Assume same number of players as team1 for fair comparison
          const blindPlayerCount = team1.length;
          team2GameTotal = botBlindAverage * blindPlayerCount;
        } else {
          team2.forEach(member => {
            const score = loadedScores[`${member.playerId}-${game}`] || 0;
            team2GameTotal += score;
          });
        }

        team1SeriesTotal += team1GameTotal;
        team2SeriesTotal += team2GameTotal;

        // Use pre-calculated handicap totals (with backup handicaps baked in) if available
        if (loadedTeamTotals && loadedTeamTotals[game]) {
          team1GameHandicap = loadedTeamTotals[game].team1Total;
          team2GameHandicap = loadedTeamTotals[game].team2Total;
        } else {
          // Calculate handicap totals
          if (isBotBlindMatch) {
            // Bot Blind match: Team1 uses handicap, Bot Blind uses scratch only
            team1.forEach(member => {
              const score = loadedScores[`${member.playerId}-${game}`] || 0;
              team1GameHandicap += score + (handicapConfig.handicap_type !== 'team_difference' ? member.handicap : 0);
            });
            team2GameHandicap = team2GameTotal; // Bot Blind has NO handicap
          } else if (handicapConfig.handicap_type === 'team_difference') {
            // Team difference: team totals are scratch; add team handicap to lower team
            team1GameHandicap = team1GameTotal;
            team2GameHandicap = team2GameTotal;
            const t1AvgSum = team1.reduce((s, m) => s + (m.setAverage || m.average || 0), 0);
            const t2AvgSum = team2.reduce((s, m) => s + (m.setAverage || m.average || 0), 0);
            const diff = Math.abs(t1AvgSum - t2AvgSum);
            const tdPct = handicapConfig.team_difference_percentage || 0;
            const teamHandicap = Math.floor(diff * tdPct / 100);
            if (t1AvgSum < t2AvgSum) team1GameHandicap += teamHandicap;
            else if (t2AvgSum < t1AvgSum) team2GameHandicap += teamHandicap;
          } else {
            // Normal match: both teams use individual handicap
            team1.forEach(member => {
              const score = loadedScores[`${member.playerId}-${game}`] || 0;
              team1GameHandicap += score + member.handicap;
            });

            team2.forEach(member => {
              const score = loadedScores[`${member.playerId}-${game}`] || 0;
              team2GameHandicap += score + member.handicap;
            });
          }
        }

        team1SeriesHandicap += team1GameHandicap;
        team2SeriesHandicap += team2GameHandicap;

        // Determine game winner based on handicap totals
        if (team1GameHandicap > team2GameHandicap) {
          gameWinners.push(1);
        } else if (team2GameHandicap > team1GameHandicap) {
          gameWinners.push(2);
        } else {
          gameWinners.push(0); // Tie
        }
      }
    }

    // Determine series winners
    const seriesWinner = team1SeriesTotal > team2SeriesTotal ? 1 : team2SeriesTotal > team1SeriesTotal ? 2 : 0;
    const seriesWinnerHandicap = team1SeriesHandicap > team2SeriesHandicap ? 1 : team2SeriesHandicap > team1SeriesHandicap ? 2 : 0;

    return {
      gameWinners,
      seriesWinner,
      seriesWinnerHandicap,
      team1SeriesTotal,
      team2SeriesTotal,
      team1SeriesHandicap,
      team2SeriesHandicap
    };
  };

  // Handle score input change
  const handleScoreChange = (playerId: number, gameNumber: number, value: string) => {
    const scoreKey = `${playerId}-${gameNumber}`;
    const numValue = parseInt(value);

    if (value === '' || (numValue >= 0 && numValue <= 300)) {
      setScores(prev => ({
        ...prev,
        [scoreKey]: value === '' ? undefined : numValue
      }));

      // Mark scores as modified if they were already saved
      if (scoresSaved) {
        setScoresModified(true);
      }
      
      // Clear loaded handicap totals since scores changed - will be recalculated on save
      setLoadedTeamGameTotals({});
    }
  };

  // Handle baker game score change (team-based)
  const handleBakerScoreChange = (teamNumber: number, gameNumber: number, value: string) => {
    const bakerKey = `team${teamNumber}-${gameNumber}`;
    const numValue = parseInt(value);

    if (value === '' || (numValue >= 0 && numValue <= 300)) {
      setBakerScores(prev => ({
        ...prev,
        [bakerKey]: value === '' ? undefined : numValue
      }));

      // Mark scores as modified if they were already saved
      if (scoresSaved) {
        setScoresModified(true);
      }
      
      // Clear loaded handicap totals since scores changed - will be recalculated on save
      setLoadedTeamGameTotals({});
    }
  };

  // DISABLED: Update handicap and average functions - can be re-enabled in the future
  /*
  // Handle handicap update
  const updateHandicap = async (playerId: number, newHandicap: number) => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/scoring/players/${playerId}/handicap`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ handicap: newHandicap })
      });

      if (response.ok) {
        // Update local state
        setTeam1Members(prev => prev.map(m =>
          m.playerId === playerId ? { ...m, handicap: newHandicap } : m
        ));
        setTeam2Members(prev => prev.map(m =>
          m.playerId === playerId ? { ...m, handicap: newHandicap } : m
        ));
      }
    } catch (error) {
      console.error('Error updating handicap:', error);
    }
  };

  // Handle average update
  const updateAverage = async (playerId: number, newAverage: number) => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/scoring/players/${playerId}/average`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ average: newAverage })
      });

      if (response.ok) {
        // Update local state
        setTeam1Members(prev => prev.map(m =>
          m.playerId === playerId ? { ...m, average: newAverage } : m
        ));
        setTeam2Members(prev => prev.map(m =>
          m.playerId === playerId ? { ...m, average: newAverage } : m
        ));
      }
    } catch (error) {
      console.error('Error updating average:', error);
    }
  };
  */

  // Handle drag and drop for team member reordering
  const handleDragStart = (member: TeamMember) => {
    setDraggedPlayer(member);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetMember: TeamMember) => {
    e.preventDefault();

    if (!draggedPlayer || draggedPlayer.playerId === targetMember.playerId) {
      setDraggedPlayer(null);
      return;
    }

    const isTeam1 = team1Members.some(m => m.playerId === draggedPlayer.playerId);
    const targetIsTeam1 = team1Members.some(m => m.playerId === targetMember.playerId);

    // Only allow reordering within the same team
    if (isTeam1 !== targetIsTeam1) {
      setDraggedPlayer(null);
      return;
    }

    if (isTeam1) {
      const newOrder = [...team1Members];
      const draggedIndex = newOrder.findIndex(m => m.playerId === draggedPlayer.playerId);
      const targetIndex = newOrder.findIndex(m => m.playerId === targetMember.playerId);

      // Remove dragged item and insert at target position
      const [draggedItem] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedItem);

      // Update sort orders
      newOrder.forEach((member, index) => {
        member.sortOrder = index + 1;
      });

      setTeam1Members(newOrder);
    } else {
      const newOrder = [...team2Members];
      const draggedIndex = newOrder.findIndex(m => m.playerId === draggedPlayer.playerId);
      const targetIndex = newOrder.findIndex(m => m.playerId === targetMember.playerId);

      // Remove dragged item and insert at target position
      const [draggedItem] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedItem);

      // Update sort orders
      newOrder.forEach((member, index) => {
        member.sortOrder = index + 1;
      });

      setTeam2Members(newOrder);
    }

    setDraggedPlayer(null);
  };

  // Save all scores
  const saveScores = async () => {
    if (!team1Members || !match) return;
    
    // For Bot Blind matches, team2Members will be empty
    const isBotBlindMatch = match.team2Id === 0;
    if (!isBotBlindMatch && !team2Members) return;

    // Validate regular game scores (exclude baker games)
    const allPlayers = isBotBlindMatch ? [...team1Members] : [...team1Members, ...team2Members];
    const regularGames = Array.from({ length: gamesCount }, (_, i) => i + 1).filter(g => !bakerGames.includes(g));
    
    const requiredScoreKeys = allPlayers.flatMap(player =>
      regularGames.map(game => `${player.playerId}-${game}`)
    );

    const missingScores = requiredScoreKeys.filter(key =>
      scores[key] === undefined || scores[key] === null
    );

    if (missingScores.length > 0) {
      setError('Please enter all scores before saving.');
      return;
    }

    // Validate baker game scores
    if (bakerGames.length > 0) {
      const missingBakerScores: string[] = [];
      bakerGames.forEach(gameNum => {
        const team1Key = `team1-${gameNum}`;
        const team2Key = `team2-${gameNum}`;
        if (bakerScores[team1Key] === undefined || bakerScores[team1Key] === null) {
          missingBakerScores.push(`Team 1 - Game ${gameNum}`);
        }
        // Skip team2 baker validation for Bot Blind matches (auto-calculated)
        if (!isBotBlindMatch && (bakerScores[team2Key] === undefined || bakerScores[team2Key] === null)) {
          missingBakerScores.push(`Team 2 - Game ${gameNum}`);
        }
      });

      if (missingBakerScores.length > 0) {
        setError(`Please enter all baker game scores: ${missingBakerScores.join(', ')}`);
        return;
      }
    }

    // Validate all scores are valid (0-300)
    const invalidScores = Object.entries(scores).filter(([key, score]) => {
      if (requiredScoreKeys.includes(key) && score !== undefined) {
        return score < 0 || score > 300;
      }
      return false;
    });

    const invalidBakerScores = Object.entries(bakerScores).filter(([, score]) => {
      if (score !== undefined) {
        return score < 0 || score > 300;
      }
      return false;
    });

    if (invalidScores.length > 0 || invalidBakerScores.length > 0) {
      setError('All scores must be between 0 and 300.');
      return;
    }

    setSavingScores(true);
    setError('');

    try {
      // Calculate team game totals WITH backup handicaps included
      const teamGameTotals: Record<string, { team1Total: number; team2Total: number }> = {};
      for (let gameNum = 1; gameNum <= gamesCount; gameNum++) {
        const team1Total = calculateTeamGameTotal(team1Members, gameNum, true); // with handicap
        
        // For Bot Blind, use the average * team1 member count (no handicap)
        let team2TotalValue = 0;
        if (isBotBlindMatch && blindConfig?.botBlindAverage) {
          team2TotalValue = blindConfig.botBlindAverage * team1Members.length;
        } else {
          const team2Total = calculateTeamGameTotal(team2Members, gameNum, true); // with handicap
          team2TotalValue = team2Total.total;
        }
        
        teamGameTotals[gameNum] = { team1Total: team1Total.total, team2Total: team2TotalValue };
      }

      // For Bot Blind baker games, auto-fill team2 baker scores
      const finalBakerScores = { ...bakerScores };
      if (isBotBlindMatch && blindConfig?.botBlindAverage) {
        bakerGames.forEach(gameNum => {
          const team2Key = `team2-${gameNum}`;
          finalBakerScores[team2Key] = blindConfig.botBlindAverage || 0;
        });
      }

      // Prepare score data for API including baker scores, backup bowlers, and calculated team totals
      const scoreData = {
        matchId: match.id,
        team1Id: match.team1Id,
        team2Id: match.team2Id,
        scores: scores,
        bakerScores: finalBakerScores,
        bakerGames: bakerGames,
        backupBowlers: backupBowlers,
        teamGameTotals: teamGameTotals, // Pre-calculated totals with backup handicaps baked in
        teamMembers: {
          team1: team1Members,
          team2: team2Members
        }
      };

      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/scoring/save-scores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(scoreData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('matchScoring.errors.failedToSaveScores'));
      }

      const results = await response.json();
      setMatchResults(results);
      setScoresSaved(true);
      setScoresModified(false); // Reset modified flag after successful save

      // Show success modal instead of alert
      setError('');
      setSuccessMessage('Scores saved successfully!');
      setShowSuccessModal(true);

    } catch (err) {
      console.error('Error saving scores:', err);
      setError(err instanceof Error ? err.message : t('matchScoring.errors.failedToSaveScores'));
    } finally {
      setSavingScores(false);
    }
  };

  const handleUpdateScores = async () => {
    if (!scoresModified || !match) return;
    
    const isBotBlindMatch = match.team2Id === 0;

    setSavingScores(true);
    setError('');

    try {
      // Calculate team game totals WITH backup handicaps included
      const teamGameTotals: Record<string, { team1Total: number; team2Total: number }> = {};
      for (let gameNum = 1; gameNum <= gamesCount; gameNum++) {
        const team1Total = calculateTeamGameTotal(team1Members, gameNum, true); // with handicap
        
        // For Bot Blind, use the average * team1 member count (no handicap)
        let team2TotalValue = 0;
        if (isBotBlindMatch && blindConfig?.botBlindAverage) {
          team2TotalValue = blindConfig.botBlindAverage * team1Members.length;
        } else {
          const team2Total = calculateTeamGameTotal(team2Members, gameNum, true); // with handicap
          team2TotalValue = team2Total.total;
        }
        
        teamGameTotals[gameNum] = { team1Total: team1Total.total, team2Total: team2TotalValue };
      }

      // For Bot Blind baker games, auto-fill team2 baker scores
      const finalBakerScores = { ...bakerScores };
      if (isBotBlindMatch && blindConfig?.botBlindAverage) {
        bakerGames.forEach(gameNum => {
          const team2Key = `team2-${gameNum}`;
          finalBakerScores[team2Key] = blindConfig.botBlindAverage || 0;
        });
      }

      // Use the new update endpoint that only updates scores without deleting Games
      const scoreData = {
        matchId: match.id,
        team1Id: match.team1Id,
        team2Id: match.team2Id,
        scores: scores,
        bakerScores: finalBakerScores,
        bakerGames: bakerGames,
        backupBowlers: backupBowlers,
        teamGameTotals: teamGameTotals, // Pre-calculated totals with backup handicaps baked in
        teamMembers: {
          team1: team1Members,
          team2: team2Members
        }
      };

      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/scoring/update-scores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(scoreData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('matchScoring.errors.failedToUpdateScores'));
      }

      const results = await response.json();

      // Convert scores to proper format for recalculation (removing undefined values)
      const cleanedScores: Record<string, number> = {};
      Object.entries(scores).forEach(([key, value]) => {
        if (value !== undefined) {
          cleanedScores[key] = value;
        }
      });

      // Recalculate winners and visual indicators with updated scores
      // Note: After update, we don't have fresh loadedTeamGameTotals yet, but the backend 
      // returns the calculated values in the response which we can use
      const recalculatedResults = recalculateWinners(
        cleanedScores, 
        team1Members, 
        team2Members,
        {} // After update, rely on backend results for now until page reload
      );

      // Update match results with both backend results and recalculated visual data
      if (recalculatedResults) {
        setMatchResults({
          ...results,
          ...recalculatedResults
        });
      } else {
        setMatchResults(results);
      }

      setScoresSaved(true);
      setScoresModified(false); // Reset modified flag after successful update

      // Show success modal for update
      setError('');
      setSuccessMessage('Scores updated successfully!');
      setShowSuccessModal(true);

    } catch (err) {
      console.error('Error updating scores:', err);
      setError(err instanceof Error ? err.message : t('matchScoring.errors.failedToUpdateScores'));
    } finally {
      setSavingScores(false);
    }
  };

  const clearScores = async () => {
    if (!scoresSaved) {
      // Just clear the form if scores weren't saved
      setScores({});
      setShowClearConfirmation(false);
      return;
    }

    if (!match) return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/scoring/clear-scores/${match.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('matchScoring.errors.failedToClearScores'));
      }

      // Reset all state
      setScores({});
      setScoresSaved(false);
      setMatchResults(null);
      setShowClearConfirmation(false);
      setError('');

      // Show success modal instead of alert
      setSuccessMessage('Scores cleared successfully!');
      setShowSuccessModal(true);

    } catch (err) {
      console.error('Error clearing scores:', err);
      setError(err instanceof Error ? err.message : t('matchScoring.errors.failedToClearScores'));
    }
  };

  // Handle point adjustments
  const handleAdjustPoints = async (teamId: number, teamName: string, pointsValue: string, operation: 'add' | 'subtract') => {
    if (!match || !pointsValue || pointsValue === '0') return;

    const points = parseInt(pointsValue);
    if (isNaN(points) || points <= 0) {
      setError('Please enter a valid positive number for points');
      return;
    }

    setAdjustingPoints(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/scoring/adjust-points`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          squadId: match.squadId,
          teamId: teamId,
          pointsToAdd: points,
          operation: operation
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('matchScoring.errors.failedToAdjustPoints'));
      }

      // Clear input and show success message
      if (teamId === match.team1Id) {
        if (operation === 'add') {
          setTeam1ExtraPoints('');
          setTeam1PointsSuccess(`Extra points added for ${teamName}!`);
        } else {
          setTeam1RemovePoints('');
          setTeam1PointsSuccess(`Points removed for ${teamName}!`);
        }
        // Clear message and refresh data after 3 seconds
        setTimeout(() => {
          setTeam1PointsSuccess('');
          fetchMatchData();
        }, 1000);
      } else {
        if (operation === 'add') {
          setTeam2ExtraPoints('');
          setTeam2PointsSuccess(`Extra points added for ${teamName}!`);
        } else {
          setTeam2RemovePoints('');
          setTeam2PointsSuccess(`Points removed for ${teamName}!`);
        }
        // Clear message and refresh data after 3 seconds
        setTimeout(() => {
          setTeam2PointsSuccess('');
          fetchMatchData();
        }, 1000);
      }

    } catch (err) {
      console.error('Error adjusting points:', err);
      setError(err instanceof Error ? err.message : t('matchScoring.errors.failedToAdjustPoints'));
    } finally {
      setAdjustingPoints(false);
    }
  };

  // Save all scores (placeholder)
  const handleSaveScores = () => {
    saveScores();
  };

  // Clear all scores
  const handleClearScores = () => {
    if (scoresSaved) {
      setShowClearConfirmation(true);
    } else {
      if (window.confirm('Are you sure you want to clear all scores?')) {
        setScores({});
      }
    }
  };

  const [showPrint, setShowPrint] = useState(false);

  useEffect(() => {
    fetchMatchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, leagueId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="flex items-center space-x-3 text-white">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          <span>{t('matchScoring.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="bg-red-500/20 border border-red-400 rounded-lg p-6 max-w-md">
          <div className="flex items-center space-x-2 mb-4">
            <div className="text-red-400 text-xl">⚠️</div>
            <h2 className="text-red-200 font-bold">{t('matchScoring.errorLoadingMatch')}</h2>
          </div>
          <p className="text-red-200 mb-4">{error}</p>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition duration-200"
          >
            {t('matchScoring.goBack')}
          </button>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="text-white">{t('matchScoring.matchNotFound')}</div>
      </div>
    );
  }

  // Calculate total games to render (regular games + baker games)
  const maxBakerGame = bakerGames.length > 0 ? Math.max(...bakerGames) : 0;
  const totalGamesToRender = Math.max(gamesCount, maxBakerGame);

  // Pre-compute team-diff handicap once for the render
  const isTeamDiffLeague = handicapConfig.handicap_type === 'team_difference';
  const { teamHandicap: tdHandicap, lowerTeamId: tdLowerTeamId } = getTeamDiffHandicap();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="text-white hover:text-blue-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-white">{t('matchScoring.title')}</h1>
                <p className="text-blue-200 mt-1">
                  {match.squadName} • {new Date(match.squadDate).toLocaleDateString()} • {match.scheduledTime}
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Match Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              {match.team1Name} {t('matchScoring.vs')} {match.team2Id === 0 ? (t('matchScoring.blind') || 'Blind') : match.team2Name}
            </h2>
            {match.team1Lane && match.team2Lane ? (
              <p className="text-blue-300 mb-2">
                🎳 {t('matchScoring.lanes')} {match.team1Lane}-{match.team2Lane} | {gamesCount} {t('matchScoring.gamesPerMatch')}
              </p>
            ) : (
              <p className="text-amber-300 mb-2">
                🚨 {t('matchScoring.noLanesAssigned')}
              </p>
            )}
            {scoresSaved && matchResults && (
              <div className="flex justify-center space-x-8 mb-4">
                <div className="text-center">
                  <div className="text-blue-300 text-sm">{match.team1Name} {t('matchScoring.points')}</div>
                  <div className="text-white font-bold text-2xl">{matchResults.team1Points}</div>
                </div>
                <div className="text-white text-xl">{t('matchScoring.vsLabel')}</div>
                <div className="text-center">
                  <div className="text-purple-300 text-sm">{match.team2Id === 0 ? (t('matchScoring.blind') || 'Blind') : match.team2Name} {t('matchScoring.points')}</div>
                  <div className="text-white font-bold text-2xl">{matchResults.team2Points}</div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
              <button
                onClick={handleSaveScores}
                disabled={savingScores || scoresSaved}
                className={`px-6 py-2 text-white rounded-lg transition duration-200 flex items-center space-x-2 ${scoresSaved
                    ? 'bg-green-800/50 cursor-not-allowed'
                    : savingScores
                      ? 'bg-gray-600/50 cursor-not-allowed'
                      : 'bg-green-600/50 hover:bg-green-700/50'
                  }`}
              >
              {savingScores ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{t('matchScoring.saving')}</span>
                </>
              ) : scoresSaved ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{t('matchScoring.saved')}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  <span>{t('matchScoring.saveScores')}</span>
                </>
              )}
            </button>

            {/* Update Button - only show when scores are saved and modified */}
            {scoresSaved && scoresModified && (
              <button
                onClick={handleUpdateScores}
                disabled={savingScores}
                className={`px-6 py-2 text-white rounded-lg transition duration-200 flex items-center space-x-2 ${savingScores
                    ? 'bg-gray-600/50 cursor-not-allowed'
                    : 'bg-blue-600/50 hover:bg-blue-700/50'
                  }`}
              >
                {savingScores ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t('matchScoring.updating')}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>{t('matchScoring.updateScores')}</span>
                  </>
                )}
              </button>
            )}

            <button
              onClick={handleClearScores}
              className="px-4 py-2 bg-red-600/40 hover:bg-red-700/40 text-white rounded-lg transition duration-200 flex items-center space-x-2"
            >

              <span>{t('matchScoring.clearScores')}</span>
            </button>
            <button
              onClick={() => setShowPrint(true)}
              className="flex items-center gap-2 bg-purple-600/50 hover:bg-purple-700/50 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {t('matchScoring.printMatch')}
            </button>
            {showPrint && (
              <ScoresPrintView
                match={{
                  ...match,
                  team1Lane: match.team1Lane ?? 0,
                  team2Lane: match.team2Lane ?? 0
                }}
                team1Members={team1Members}
                team2Members={team2Members}
                gamesCount={gamesCount}
                scores={scores}
                bakerScores={bakerScores}
                bakerGames={bakerGames}
                matchResults={matchResults ?? undefined}
                leagueName={leagueName}
                onClose={() => setShowPrint(false)}
              />
            )}

          </div>
        </div>

        {/* Unpaid Members Banner */}
        {unpaidMemberIds.size > 0 && (
          <div className="mb-6 px-4 py-3 bg-orange-500/10 border border-orange-500/20 rounded-xl">
            <p className="text-orange-400 text-sm font-semibold mb-2">⚠ Members with outstanding payments</p>
            <div className="flex flex-wrap gap-1">
              {[...team1Members, ...team2Members]
                .filter(m => unpaidMemberIds.has(m.playerId))
                .map(m => (
                  <span key={m.playerId} className="bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded-full px-2 py-0.5 text-xs">
                    {m.playerName}
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Scoring Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Team 1 */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <h3 className="text-xl font-bold text-white mb-2 text-center">{match.team1Name}</h3>
            {/* Team-diff handicap badge */}
            {isTeamDiffLeague && (
              <div className="text-center mb-4">
                {tdLowerTeamId === match.team1Id && tdHandicap > 0
                  ? <span className="inline-flex items-center gap-1 bg-green-500/20 border border-green-400/40 text-green-200 px-3 py-1 rounded-full text-xs font-semibold">+{tdHandicap} HDCP/game (receives)</span>
                  : <span className="inline-flex items-center gap-1 bg-white/10 border border-white/20 text-gray-400 px-3 py-1 rounded-full text-xs">No HDCP</span>
                }
              </div>
            )}

            {/* Team 1 Members */}
            <div className="space-y-4 mb-6">
              {team1Members.map((member, index) => {
                const playerHasBackup = hasBackup(member.playerId);
                const backupInfo = getBackupInfo(member.playerId);
                
                return (
                <div key={member.playerId} className="space-y-2">
                  {/* Original Bowler Card */}
                  <div
                    draggable
                    onDragStart={() => handleDragStart(member)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, member)}
                    className={`bg-white/5 rounded-lg p-4 border border-white/10 cursor-move hover:bg-white/10 transition duration-200 ${playerHasBackup ? 'opacity-50' : ''}`}
                  >
                    {/* Backup and Blind Buttons */}
                    {!playerHasBackup && (
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <button
                          onClick={() => {
                            setShowBackupSelector({playerId: member.playerId, playerName: member.playerName});
                            fetchAvailableBowlers('');
                          }}
                          className="px-3 py-1.5 bg-amber-600/30 hover:bg-amber-600/50 border border-amber-400/30 rounded text-amber-200 text-xs transition duration-200 flex items-center justify-center space-x-1"
                          title="Log a backup bowler for this absent player"
                        >
                          <span>🔄</span>
                          <span>{t('matchScoring.logBackupBowler')}</span>
                        </button>
                        <button
                          onClick={() => handleUseBlind(member)}
                          className="px-3 py-1.5 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-400/30 rounded text-purple-200 text-xs transition duration-200 flex items-center justify-center space-x-1"
                          title={t('matchScoring.useBlindTitle')}
                        >
                          <span>👻</span>
                          <span>{t('matchScoring.useBlind')}</span>
                        </button>
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="text-white font-semibold flex items-center gap-2 flex-wrap">
                            {member.playerName}
                            {unpaidMemberIds.has(member.playerId) && (
                              <span className="text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded-full">Unpaid</span>
                            )}
                          </h4>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-1">
                            <span className="text-blue-300">{isTeamDiffLeague ? 'Calc Avg' : t('matchScoring.avg')}:</span>
                            <span className="text-white">{member.average}</span>
                            {/* DISABLED: Click-to-edit average - can be re-enabled in the future */}
                            {/* {editingAverage === member.playerId ? (
                              <input
                                type="number"
                                min="0"
                                max="300"
                                value={member.average}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  setTeam1Members(prev => prev.map(m =>
                                    m.playerId === member.playerId ? { ...m, average: value } : m
                                  ));
                                }}
                                onBlur={() => {
                                  updateAverage(member.playerId, member.average);
                                  setEditingAverage(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateAverage(member.playerId, member.average);
                                    setEditingAverage(null);
                                  }
                                }}
                                className="w-16 px-1 py-0.5 bg-white/10 border border-blue-400 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                autoFocus
                              />
                            ) : (
                              <span
                                className="text-white cursor-pointer hover:text-blue-300"
                                onClick={() => setEditingAverage(member.playerId)}
                              >
                                {member.average}
                              </span>
                            )} */}
                          </div>
                          {isTeamDiffLeague ? (
                            <div className="flex items-center space-x-1">
                              <span className="text-amber-300">Set:</span>
                              <span className="text-white font-medium">{member.setAverage || '—'}</span>
                            </div>
                          ) : (
                          <div className="flex items-center space-x-1">
                            <span className="text-green-300">{t('matchScoring.hdcp')}:</span>
                            <span className="text-white">{member.handicap}</span>
                            {/* DISABLED: Click-to-edit handicap - can be re-enabled in the future */}
                            {/* {editingHandicap === member.playerId ? (
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={member.handicap}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  setTeam1Members(prev => prev.map(m =>
                                    m.playerId === member.playerId ? { ...m, handicap: value } : m
                                  ));
                                }}
                                onBlur={() => {
                                  updateHandicap(member.playerId, member.handicap);
                                  setEditingHandicap(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateHandicap(member.playerId, member.handicap);
                                    setEditingHandicap(null);
                                  }
                                }}
                                className="w-12 px-1 py-0.5 bg-white/10 border border-green-400 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                                autoFocus
                              />
                            ) : (
                              <span
                                className="text-white cursor-pointer hover:text-green-300"
                                onClick={() => setEditingHandicap(member.playerId)}
                              >
                                {member.handicap}
                              </span>
                            )} */}
                          </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      ⋮⋮ Drag to reorder
                    </div>
                  </div>

                  {/* Game Scores */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {Array.from({ length: gamesCount }, (_, gameIndex) => {
                      const gameNumber = gameIndex + 1;
                      
                      // Skip baker games - they are team-based, not per player
                      if (bakerGames.includes(gameNumber)) {
                        return null;
                      }
                      
                      const scoreKey = `${member.playerId}-${gameNumber}`;
                      const score = scores[scoreKey];
                      return (
                        <div key={gameNumber} className="text-center">
                          <label className="block text-xs text-blue-300 mb-1">{t('matchScoring.game')} {gameNumber}</label>
                          <input
                            type="number"
                            min="0"
                            max="300"
                            value={score || ''}
                            onChange={(e) => handleScoreChange(member.playerId, gameNumber, e.target.value)}
                            disabled={playerHasBackup}
                            className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="0"
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Player Series Totals */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-blue-300">{t('matchScoring.series')}</div>
                      <div className="text-white font-bold">
                        {calculatePlayerSeries(member.playerId).total}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-green-300">{t('matchScoring.withHandicap')}</div>
                      <div className="text-white font-bold">
                        {calculatePlayerSeries(member.playerId, true).total}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Backup Bowler Card */}
                {playerHasBackup && backupInfo && (
                  <div className="bg-amber-900/20 rounded-lg p-4 border-2 border-amber-400/40">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-amber-400 text-lg">🔄</span>
                        <div>
                          <div className="text-xs text-amber-300">{t('matchScoring.backupFor')} {member.playerName}</div>
                          <h4 className="text-white font-semibold">
                            {backupInfo.backupBowlerName === 'Blind' ? t('matchScoring.blind') : backupInfo.backupBowlerName}
                          </h4>
                          <div className="flex items-center space-x-4 text-sm">
                            <div className="flex items-center space-x-1">
                              <span className="text-amber-300">{t('matchScoring.avg')}:</span>
                              <span className="text-white">{backupInfo.average}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-amber-300">{t('matchScoring.hdcp')}:</span>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={backupInfo.handicap}
                                onChange={(e) => handleUpdateBackupHandicap(member.playerId, parseInt(e.target.value) || 0)}
                                className="w-16 px-2 py-0.5 bg-amber-900/30 border border-amber-400/30 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveBackup(member.playerId)}
                        className="p-2 bg-red-600/40 hover:bg-red-700/40 rounded transition duration-200"
                        title={t('matchScoring.removeBackup')}
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Backup Game Scores */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {Array.from({ length: gamesCount }, (_, gameIndex) => {
                        const gameNumber = gameIndex + 1;
                        
                        if (bakerGames.includes(gameNumber)) {
                          return null;
                        }
                        
                        const scoreKey = `${member.playerId}-${gameNumber}`;
                        const score = scores[scoreKey];
                        return (
                          <div key={gameNumber} className="text-center">
                            <label className="block text-xs text-amber-300 mb-1">{t('matchScoring.game')} {gameNumber}</label>
                            <input
                              type="number"
                              min="0"
                              max="300"
                              value={score || ''}
                              onChange={(e) => handleScoreChange(member.playerId, gameNumber, e.target.value)}
                              className="w-full px-2 py-1 bg-amber-900/30 border border-amber-400/30 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400"
                              placeholder="0"
                            />
                          </div>
                        );
                      })}
                    </div>

                    {/* Backup Series Totals */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-amber-300">{t('matchScoring.series')}</div>
                        <div className="text-white font-bold">
                          {calculatePlayerSeries(member.playerId).total}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-amber-300">{t('matchScoring.withHandicap')}</div>
                        <div className="text-white font-bold">
                          {calculatePlayerSeries(member.playerId, true).total}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )})}
            </div>

            {/* Baker Game Input for Team 1 */}
            {bakerGames.length > 0 && (
              <div className="bg-purple-900/20 rounded-lg p-4 border border-purple-400/30">
                <h4 className="text-white font-bold mb-3 text-center flex items-center justify-center gap-2">
                  <span>🎳</span>
                  <span>{t('matchScoring.bakerGames')}</span>
                </h4>
                <p className="text-purple-300 text-xs text-center mb-3">
                  {t('matchScoring.bakerGameDescription')}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {bakerGames.map((gameNumber) => {
                    const bakerKey = `team1-${gameNumber}`;
                    const bakerScore = bakerScores[bakerKey];
                    return (
                      <div key={gameNumber} className="text-center">
                        <label className="block text-xs text-purple-300 mb-1">{t('matchScoring.game')} {gameNumber}</label>
                        <input
                          type="number"
                          min="0"
                          max="300"
                          value={bakerScore || ''}
                          onChange={(e) => handleBakerScoreChange(1, gameNumber, e.target.value)}
                          className="w-full px-2 py-1 bg-purple-900/30 border border-purple-400/30 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-400"
                          placeholder="0"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Team 1 Totals */}
            <div className="bg-blue-600/20 rounded-lg p-4 border border-blue-400/30">
              <h4 className="text-white font-bold mb-3 text-center">{t('matchScoring.teamTotals')}</h4>

              {/* Game Totals */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {Array.from({ length: totalGamesToRender }, (_, gameIndex) => {
                  const gameNumber = gameIndex + 1;
                  const gameTotal = calculateTeamGameTotal(team1Members, gameNumber);
                  const gameHandicapTotal = calculateTeamGameTotal(team1Members, gameNumber, true);
                  const gameWinner = getGameWinner(gameNumber);
                  return (
                    <div key={gameNumber} className={`text-center relative ${scoresSaved && gameWinner === 2 ? 'opacity-50 blur-[1px]' : ''
                      }`}>
                      {scoresSaved && gameWinner === 1 && (
                        <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 z-10">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      {scoresSaved && gameWinner === 2 && (
                        <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1 z-10">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      )}
                      <div className="text-xs text-blue-300 mb-1">{t('matchScoring.game')} {gameNumber}</div>
                      <div className="text-white font-bold">{gameTotal.total}</div>
                      <div className="text-green-300 text-sm">{t('matchScoring.withHandicap')}: <span className="font-bold text-white">{gameHandicapTotal.total}</span></div>
                    </div>
                  );
                })}
              </div>

              {/* Series Totals */}
              <div className={`grid grid-cols-2 gap-4 text-center border-t border-blue-400/30 pt-3 relative ${scoresSaved && getSeriesWinnerHandicap() === 2 ? 'opacity-50 blur-[1px]' : ''
                }`}>
                {scoresSaved && getSeriesWinnerHandicap() === 1 && (
                  <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-2 z-10">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                {scoresSaved && getSeriesWinnerHandicap() === 2 && (
                  <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-2 z-10">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                <div>
                  <div className="text-blue-300 text-sm">{t('matchScoring.teamSeries')}</div>
                  <div className="text-white font-bold text-xl">
                    {calculateTeamSeries(team1Members)}
                  </div>
                </div>
                <div className={`relative ${scoresSaved && getSeriesWinnerHandicap() === 2 ? 'opacity-50 blur-[1px]' : ''
                  }`}>
                  {scoresSaved && getSeriesWinnerHandicap() === 1 && (
                    <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  {scoresSaved && getSeriesWinnerHandicap() === 2 && (
                    <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )}
                  <div className="text-green-300 text-sm">{t('matchScoring.withHandicap')}</div>
                  <div className="text-white font-bold text-xl">
                    {calculateTeamSeries(team1Members, true)}
                  </div>
                </div>
              </div>
            </div>

            {/* Point Adjustment Controls - Team 1 */}
            {scoresSaved && (
              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column - Inputs */}
                <div className="space-y-3">
                  {/* Add Extra Points */}
                  <div className="flex items-center space-x-2">
                    <label className="text-blue-300 text-sm whitespace-nowrap">{t('matchScoring.extraPoints')}</label>
                    <input
                      type="number"
                      min="1"
                      value={team1ExtraPoints}
                      onChange={(e) => setTeam1ExtraPoints(e.target.value)}
                      disabled={adjustingPoints}
                      className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="0"
                    />
                    {team1ExtraPoints && parseInt(team1ExtraPoints) > 0 && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleAdjustPoints(match!.team1Id, match!.team1Name, team1ExtraPoints, 'add')}
                          disabled={adjustingPoints}
                          className="p-1 bg-green-600/50 hover:bg-green-700/50 rounded transition duration-200"
                          title={t('matchScoring.confirm')}
                        >
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setTeam1ExtraPoints('')}
                          disabled={adjustingPoints}
                          className="p-1 bg-red-600/50 hover:bg-red-700/50 rounded transition duration-200"
                          title={t('matchScoring.cancel')}
                        >
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Remove Points */}
                  <div className="flex items-center space-x-2">
                    <label className="text-red-300 text-sm whitespace-nowrap">{t('matchScoring.removePoints')}</label>
                    <input
                      type="number"
                      min="1"
                      value={team1RemovePoints}
                      onChange={(e) => setTeam1RemovePoints(e.target.value)}
                      disabled={adjustingPoints}
                      className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                      placeholder="0"
                    />
                    {team1RemovePoints && parseInt(team1RemovePoints) > 0 && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleAdjustPoints(match!.team1Id, match!.team1Name, team1RemovePoints, 'subtract')}
                          disabled={adjustingPoints}
                          className="p-1 bg-green-600/50 hover:bg-green-700/50 rounded transition duration-200"
                          title={t('matchScoring.confirm')}
                        >
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setTeam1RemovePoints('')}
                          disabled={adjustingPoints}
                          className="p-1 bg-red-600/50 hover:bg-red-700/50 rounded transition duration-200"
                          title={t('matchScoring.cancel')}
                        >
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Success Message */}
                  {team1PointsSuccess && (
                    <div className="text-green-400 text-sm font-medium">
                      {team1PointsSuccess}
                    </div>
                  )}
                </div>

                {/* Right Column - Formula Card */}
                {match.customFormula && (
                  <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-400/20">
                    <div className="flex items-center space-x-1 mb-1">
                      <svg className="w-3 h-3 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-amber-300 text-xs font-semibold">{t('matchScoring.pointFormula')}</span>
                    </div>
                    <p className="text-amber-100 text-xs leading-relaxed whitespace-pre-wrap">
                      {match.customFormula}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Team 2 */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <h3 className="text-xl font-bold text-white mb-2 text-center">
              {match.team2Id === 0 ? (t('matchScoring.blind') || 'Blind') : match.team2Name}
            </h3>
            {/* Team-diff handicap badge */}
            {isTeamDiffLeague && match.team2Id !== 0 && (
              <div className="text-center mb-4">
                {tdLowerTeamId === match.team2Id && tdHandicap > 0
                  ? <span className="inline-flex items-center gap-1 bg-green-500/20 border border-green-400/40 text-green-200 px-3 py-1 rounded-full text-xs font-semibold">+{tdHandicap} HDCP/game (receives)</span>
                  : <span className="inline-flex items-center gap-1 bg-white/10 border border-white/20 text-gray-400 px-3 py-1 rounded-full text-xs">No HDCP</span>
                }
              </div>
            )}

            {/* Bot Blind Section */}
            {match.team2Id === 0 ? (
              <div className="bg-purple-500/20 border border-purple-400/40 rounded-lg p-6 text-center">
                <h4 className="text-2xl font-bold text-purple-200 mb-2">{t('matchScoring.blind') || 'Blind'}</h4>
                <p className="text-purple-300 text-sm mb-4">{t('matchScoring.botBlind.description')}</p>
                {blindConfig?.botBlindAverage ? (
                  <div className="bg-purple-600/30 rounded-lg p-4">
                    <p className="text-purple-200 text-sm mb-1">{t('matchScoring.botBlind.averageScore')}</p>
                    <p className="text-4xl font-bold text-white">{blindConfig.botBlindAverage}</p>
                    <p className="text-purple-300 text-xs mt-2">{t('matchScoring.botBlind.eachGame')}</p>
                  </div>
                ) : (
                  <p className="text-yellow-300 text-sm">{t('matchScoring.botBlind.notConfigured')}</p>
                )}
              </div>
            ) : (
              /* Team 2 Members */
              <div className="space-y-4 mb-6">
              {team2Members.map((member, index) => {
                const playerHasBackup = hasBackup(member.playerId);
                const backupInfo = getBackupInfo(member.playerId);
                
                return (
                <div key={member.playerId} className="space-y-2">
                  {/* Original Bowler Card */}
                  <div
                    draggable
                    onDragStart={() => handleDragStart(member)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, member)}
                    className={`bg-white/5 rounded-lg p-4 border border-white/10 cursor-move hover:bg-white/10 transition duration-200 ${playerHasBackup ? 'opacity-50' : ''}`}
                  >
                    {/* Backup and Blind Buttons */}
                    {!playerHasBackup && (
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <button
                          onClick={() => {
                            setShowBackupSelector({playerId: member.playerId, playerName: member.playerName});
                            fetchAvailableBowlers('');
                          }}
                          className="px-3 py-1.5 bg-amber-600/30 hover:bg-amber-600/50 border border-amber-400/30 rounded text-amber-200 text-xs transition duration-200 flex items-center justify-center space-x-1"
                          title="Log a backup bowler for this absent player"
                        >
                          <span>🔄</span>
                          <span>{t('matchScoring.logBackupBowler')}</span>
                        </button>
                        <button
                          onClick={() => handleUseBlind(member)}
                          className="px-3 py-1.5 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-400/30 rounded text-purple-200 text-xs transition duration-200 flex items-center justify-center space-x-1"
                          title={t('matchScoring.useBlindTitle')}
                        >
                          <span>👻</span>
                          <span>{t('matchScoring.useBlind')}</span>
                        </button>
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-purple-600 rounded-full text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="text-white font-semibold flex items-center gap-2 flex-wrap">
                            {member.playerName}
                            {unpaidMemberIds.has(member.playerId) && (
                              <span className="text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded-full">Unpaid</span>
                            )}
                          </h4>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-1">
                            <span className="text-blue-300">{isTeamDiffLeague ? 'Calc Avg' : t('matchScoring.avg')}:</span>
                            <span className="text-white">{member.average}</span>
                            {/* DISABLED: Click-to-edit average - can be re-enabled in the future */}
                            {/* {editingAverage === member.playerId ? (
                              <input
                                type="number"
                                min="0"
                                max="300"
                                value={member.average}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  setTeam2Members(prev => prev.map(m =>
                                    m.playerId === member.playerId ? { ...m, average: value } : m
                                  ));
                                }}
                                onBlur={() => {
                                  updateAverage(member.playerId, member.average);
                                  setEditingAverage(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateAverage(member.playerId, member.average);
                                    setEditingAverage(null);
                                  }
                                }}
                                className="w-16 px-1 py-0.5 bg-white/10 border border-blue-400 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                autoFocus
                              />
                            ) : (
                              <span
                                className="text-white cursor-pointer hover:text-blue-300"
                                onClick={() => setEditingAverage(member.playerId)}
                              >
                                {member.average}
                              </span>
                            )} */}
                          </div>
                          {isTeamDiffLeague ? (
                            <div className="flex items-center space-x-1">
                              <span className="text-amber-300">Set:</span>
                              <span className="text-white font-medium">{member.setAverage || '—'}</span>
                            </div>
                          ) : (
                          <div className="flex items-center space-x-1">
                            <span className="text-green-300">{t('matchScoring.hdcp')}:</span>
                            <span className="text-white">{member.handicap}</span>
                            {/* DISABLED: Click-to-edit handicap - can be re-enabled in the future */}
                            {/* {editingHandicap === member.playerId ? (
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={member.handicap}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  setTeam2Members(prev => prev.map(m =>
                                    m.playerId === member.playerId ? { ...m, handicap: value } : m
                                  ));
                                }}
                                onBlur={() => {
                                  updateHandicap(member.playerId, member.handicap);
                                  setEditingHandicap(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateHandicap(member.playerId, member.handicap);
                                    setEditingHandicap(null);
                                  }
                                }}
                                className="w-12 px-1 py-0.5 bg-white/10 border border-green-400 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                                autoFocus
                              />
                            ) : (
                              <span
                                className="text-white cursor-pointer hover:text-green-300"
                                onClick={() => setEditingHandicap(member.playerId)}
                              >
                                {member.handicap}
                              </span>
                            )} */}
                          </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      ⋮⋮ Drag to reorder
                    </div>
                  </div>

                  {/* Game Scores */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {Array.from({ length: gamesCount }, (_, gameIndex) => {
                      const gameNumber = gameIndex + 1;
                      
                      // Skip baker games - they are team-based, not per player
                      if (bakerGames.includes(gameNumber)) {
                        return null;
                      }
                      
                      const scoreKey = `${member.playerId}-${gameNumber}`;
                      const score = scores[scoreKey];
                      return (
                        <div key={gameNumber} className="text-center">
                          <label className="block text-xs text-purple-300 mb-1">{t('matchScoring.game')} {gameNumber}</label>
                          <input
                            type="number"
                            min="0"
                            max="300"
                            value={score || ''}
                            onChange={(e) => handleScoreChange(member.playerId, gameNumber, e.target.value)}
                            disabled={playerHasBackup}
                            className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            placeholder="0"
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Player Series Totals */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-blue-300">{t('matchScoring.series')}</div>
                      <div className="text-white font-bold">
                        {calculatePlayerSeries(member.playerId).total}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-green-300">{t('matchScoring.withHandicap')}</div>
                      <div className="text-white font-bold">
                        {calculatePlayerSeries(member.playerId, true).total}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Backup Bowler Card */}
                {playerHasBackup && backupInfo && (
                  <div className="bg-amber-900/20 rounded-lg p-4 border-2 border-amber-400/40">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-amber-400 text-lg">🔄</span>
                        <div>
                          <div className="text-xs text-amber-300">{t('matchScoring.backupFor')} {member.playerName}</div>
                          <h4 className="text-white font-semibold">
                            {backupInfo.backupBowlerName === 'Blind' ? t('matchScoring.blind') : backupInfo.backupBowlerName}
                          </h4>
                          <div className="flex items-center space-x-4 text-sm">
                            <div className="flex items-center space-x-1">
                              <span className="text-amber-300">{t('matchScoring.avg')}:</span>
                              <span className="text-white">{backupInfo.average}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-amber-300">{t('matchScoring.hdcp')}:</span>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={backupInfo.handicap}
                                onChange={(e) => handleUpdateBackupHandicap(member.playerId, parseInt(e.target.value) || 0)}
                                className="w-16 px-2 py-0.5 bg-amber-900/30 border border-amber-400/30 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveBackup(member.playerId)}
                        className="p-2 bg-red-600/40 hover:bg-red-700/40 rounded transition duration-200"
                        title={t('matchScoring.removeBackup')}
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Backup Game Scores */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {Array.from({ length: gamesCount }, (_, gameIndex) => {
                        const gameNumber = gameIndex + 1;
                        
                        if (bakerGames.includes(gameNumber)) {
                          return null;
                        }
                        
                        const scoreKey = `${member.playerId}-${gameNumber}`;
                        const score = scores[scoreKey];
                        return (
                          <div key={gameNumber} className="text-center">
                            <label className="block text-xs text-amber-300 mb-1">{t('matchScoring.game')} {gameNumber}</label>
                            <input
                              type="number"
                              min="0"
                              max="300"
                              value={score || ''}
                              onChange={(e) => handleScoreChange(member.playerId, gameNumber, e.target.value)}
                              className="w-full px-2 py-1 bg-amber-900/30 border border-amber-400/30 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-400"
                              placeholder="0"
                            />
                          </div>
                        );
                      })}
                    </div>

                    {/* Backup Series Totals */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-amber-300">{t('matchScoring.series')}</div>
                        <div className="text-white font-bold">
                          {calculatePlayerSeries(member.playerId).total}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-amber-300">{t('matchScoring.withHandicap')}</div>
                        <div className="text-white font-bold">
                          {calculatePlayerSeries(member.playerId, true).total}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )})}
            </div>
            )}

            {/* Baker Game Input for Team 2 - Skip for Bot Blind */}
            {bakerGames.length > 0 && match.team2Id !== 0 && (
              <div className="bg-purple-900/20 rounded-lg p-4 border border-purple-400/30">
                <h4 className="text-white font-bold mb-3 text-center flex items-center justify-center gap-2">
                  <span>🎳</span>
                  <span>{t('matchScoring.bakerGames')}</span>
                </h4>
                <p className="text-purple-300 text-xs text-center mb-3">
                  {t('matchScoring.bakerGameDescription')}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {bakerGames.map((gameNumber) => {
                    const bakerKey = `team2-${gameNumber}`;
                    const bakerScore = bakerScores[bakerKey];
                    return (
                      <div key={gameNumber} className="text-center">
                        <label className="block text-xs text-purple-300 mb-1">{t('matchScoring.game')} {gameNumber}</label>
                        <input
                          type="number"
                          min="0"
                          max="300"
                          value={bakerScore || ''}
                          onChange={(e) => handleBakerScoreChange(2, gameNumber, e.target.value)}
                          className="w-full px-2 py-1 bg-purple-900/30 border border-purple-400/30 rounded text-white text-center focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-400"
                          placeholder="0"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Team 2 Totals */}
            <div className="bg-purple-600/20 rounded-lg p-4 border border-purple-400/30">
              <h4 className="text-white font-bold mb-3 text-center">{t('matchScoring.teamTotals')}</h4>

              {/* Game Totals */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {Array.from({ length: totalGamesToRender }, (_, gameIndex) => {
                  const gameNumber = gameIndex + 1;
                  
                  // For Bot Blind, calculate from average instead of querying members
                  let gameTotal, gameHandicapTotal;
                  if (match?.team2Id === 0 && blindConfig?.botBlindAverage) {
                    const botBlindGameTotal = blindConfig.botBlindAverage * team1Members.length;
                    gameTotal = { total: botBlindGameTotal, playersWithScores: team1Members.length };
                    gameHandicapTotal = { total: botBlindGameTotal, playersWithScores: team1Members.length }; // No handicap for Bot Blind
                  } else {
                    gameTotal = calculateTeamGameTotal(team2Members, gameNumber);
                    gameHandicapTotal = calculateTeamGameTotal(team2Members, gameNumber, true);
                  }
                  
                  const gameWinner = getGameWinner(gameNumber);
                  return (
                    <div key={gameNumber} className={`text-center relative ${scoresSaved && gameWinner === 1 ? 'opacity-50 blur-[1px]' : ''
                      }`}>
                      {scoresSaved && gameWinner === 2 && (
                        <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 z-10">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      {scoresSaved && gameWinner === 1 && (
                        <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1 z-10">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      )}
                      <div className="text-xs text-purple-300 mb-1">{t('matchScoring.game')} {gameNumber}</div>
                      <div className="text-white font-bold">{gameTotal.total}</div>
                      <div className="text-green-300 text-sm">{t('matchScoring.withHandicap')}: <span className="font-bold text-white">{gameHandicapTotal.total}</span></div>
                    </div>
                  );
                })}
              </div>

              {/* Series Totals */}
              <div className={`grid grid-cols-2 gap-4 text-center border-t border-purple-400/30 pt-3 relative ${scoresSaved && getSeriesWinnerHandicap() === 1 ? 'opacity-50 blur-[1px]' : ''
                }`}>
                {scoresSaved && getSeriesWinnerHandicap() === 2 && (
                  <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-2 z-10">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                {scoresSaved && getSeriesWinnerHandicap() === 1 && (
                  <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-2 z-10">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                <div>
                  <div className="text-purple-300 text-sm">{t('matchScoring.teamSeries')}</div>
                  <div className="text-white font-bold text-xl">
                    {match?.team2Id === 0 && blindConfig?.botBlindAverage
                      ? (blindConfig.botBlindAverage * team1Members.length * gamesCount)
                      : calculateTeamSeries(team2Members)
                    }
                  </div>
                </div>
                <div className={`relative ${scoresSaved && getSeriesWinnerHandicap() === 1 ? 'opacity-50 blur-[1px]' : ''
                  }`}>
                  {scoresSaved && getSeriesWinnerHandicap() === 2 && (
                    <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  {scoresSaved && getSeriesWinnerHandicap() === 1 && (
                    <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )}
                  <div className="text-green-300 text-sm">{t('matchScoring.withHandicap')}</div>
                  <div className="text-white font-bold text-xl">
                    {match?.team2Id === 0 && blindConfig?.botBlindAverage
                      ? (blindConfig.botBlindAverage * team1Members.length * gamesCount)
                      : calculateTeamSeries(team2Members, true)
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Point Adjustment Controls - Team 2 */}
            {scoresSaved && (
              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column - Inputs */}
                <div className="space-y-3">
                  {/* Add Extra Points */}
                  <div className="flex items-center space-x-2">
                    <label className="text-purple-300 text-sm whitespace-nowrap">{t('matchScoring.extraPoints')}</label>
                    <input
                      type="number"
                      min="1"
                      value={team2ExtraPoints}
                      onChange={(e) => setTeam2ExtraPoints(e.target.value)}
                      disabled={adjustingPoints}
                      className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                      placeholder="0"
                    />
                    {team2ExtraPoints && parseInt(team2ExtraPoints) > 0 && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleAdjustPoints(match!.team2Id, match!.team2Name, team2ExtraPoints, 'add')}
                          disabled={adjustingPoints}
                          className="p-1 bg-green-600/50 hover:bg-green-700/50 rounded transition duration-200"
                          title={t('matchScoring.confirm')}
                        >
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setTeam2ExtraPoints('')}
                          disabled={adjustingPoints}
                          className="p-1 bg-red-600/50 hover:bg-red-700/50 rounded transition duration-200"
                          title={t('matchScoring.cancel')}
                        >
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Remove Points */}
                  <div className="flex items-center space-x-2">
                    <label className="text-red-300 text-sm whitespace-nowrap">{t('matchScoring.removePoints')}</label>
                    <input
                      type="number"
                      min="1"
                      value={team2RemovePoints}
                      onChange={(e) => setTeam2RemovePoints(e.target.value)}
                      disabled={adjustingPoints}
                      className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                      placeholder="0"
                    />
                    {team2RemovePoints && parseInt(team2RemovePoints) > 0 && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleAdjustPoints(match!.team2Id, match!.team2Name, team2RemovePoints, 'subtract')}
                          disabled={adjustingPoints}
                          className="p-1 bg-green-600/50 hover:bg-green-700/50 rounded transition duration-200"
                          title={t('matchScoring.confirm')}
                        >
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setTeam2RemovePoints('')}
                          disabled={adjustingPoints}
                          className="p-1 bg-red-600/50 hover:bg-red-700/50 rounded transition duration-200"
                          title={t('matchScoring.cancel')}
                        >
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Success Message */}
                  {team2PointsSuccess && (
                    <div className="text-green-400 text-sm font-medium">
                      {team2PointsSuccess}
                    </div>
                  )}
                </div>

                {/* Right Column - Formula Card */}
                {match.customFormula && (
                  <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-400/20">
                    <div className="flex items-center space-x-1 mb-1">
                      <svg className="w-3 h-3 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-amber-300 text-xs font-semibold">{t('matchScoring.pointFormula')}</span>
                    </div>
                    <p className="text-amber-100 text-xs leading-relaxed whitespace-pre-wrap">
                      {match.customFormula}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clear Scores Confirmation Modal */}
      {showClearConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="text-red-500 text-2xl">⚠️</div>
              <h3 className="text-xl font-bold text-gray-900">{t('matchScoring.modals.clearConfirmation.title')}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {t('matchScoring.modals.clearConfirmation.message')}
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={() => setShowClearConfirmation(false)}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition duration-200"
              >
                {t('matchScoring.modals.clearConfirmation.cancel')}
              </button>
              <button
                onClick={clearScores}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition duration-200"
              >
                {t('matchScoring.modals.clearConfirmation.clearScores')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-black/70 rounded-xl p-6 max-w-md mx-4">
            <div className="flex justify-center space-x-3 mb-4">
              <h3 className="text-xl font-bold text-white">✔️</h3>
            </div>
            <p className="text-white mb-6">
              {successMessage}
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-4 py-2 bg-purple-600/50 hover:bg-purple-700/50 text-white rounded-lg transition duration-200"
              >
                {t('matchScoring.modals.success.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup Bowler Selector Modal */}
      {showBackupSelector && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-xl border border-white/20 max-w-2xl w-full max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-white/20">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-2xl font-bold text-white flex items-center space-x-2">
                  <span>🔄</span>
                  <span>{t('matchScoring.selectBackupBowler')}</span>
                </h3>
                <button
                  onClick={() => {
                    setShowBackupSelector(null);
                    setBowlerSearchTerm('');
                    setBowlerFetchError(null);
                    setShowAddBowlerInBackup(false);
                  }}
                  className="text-gray-400 hover:text-white transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-blue-300 text-sm">
                {t('matchScoring.backupFor')}: <span className="font-semibold text-white">{showBackupSelector.playerName}</span>
              </p>
            </div>

            {/* Search + Add New */}
            <div className="p-4 border-b border-white/20 space-y-2">
              <input
                type="text"
                placeholder={t('matchScoring.searchBackupBowler')}
                value={bowlerSearchTerm}
                onChange={(e) => {
                  setBowlerSearchTerm(e.target.value);
                  fetchAvailableBowlers(e.target.value);
                }}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              {/* Error banner */}
              {bowlerFetchError && (
                <div className="px-3 py-2 bg-red-500/20 border border-red-400/40 rounded-lg text-red-300 text-sm flex items-center justify-between">
                  <span>⚠️ {bowlerFetchError}</span>
                  <button
                    onClick={() => fetchAvailableBowlers(bowlerSearchTerm)}
                    className="text-red-200 underline text-xs ml-2"
                  >Retry</button>
                </div>
              )}
              <button
                onClick={() => setShowAddBowlerInBackup(true)}
                className="w-full py-2 px-4 bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-400/40 rounded-lg text-emerald-200 text-sm font-medium transition flex items-center justify-center gap-2"
              >
                <span>➕</span>
                <span>Add New Bowler to System</span>
              </button>
            </div>

            {/* Bowlers List */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingBowlers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              ) : availableBowlers.length === 0 && !bowlerFetchError ? (
                <div className="text-center py-8 text-gray-400">
                  {bowlerSearchTerm
                    ? `No bowlers found matching "${bowlerSearchTerm}"`
                    : t('matchScoring.noBackupBowlersFound')}
                </div>
              ) : (
                <div className="space-y-2">
                  {availableBowlers.map((bowler: any) => (
                    <button
                      key={bowler.id}
                      onClick={() => handleSelectBackup(bowler, showBackupSelector.playerId)}
                      className="w-full bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-4 transition duration-200 text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-semibold">{bowler.name}</h4>
                          <div className="flex items-center space-x-4 text-sm mt-1">
                            <span className="text-blue-300">
                              {t('matchScoring.avg')}: <span className="text-white">{bowler.average || 0}</span>
                            </span>
                            <span className="text-green-300">
                              {t('matchScoring.hdcp')}: <span className="text-white">{bowler.handicap || 0}</span>
                            </span>
                          </div>
                          {bowler.email && <p className="text-gray-400 text-xs mt-1">{bowler.email}</p>}
                        </div>
                        <div className="text-blue-400">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/20">
              <button
                onClick={() => {
                  setShowBackupSelector(null);
                  setBowlerSearchTerm('');
                  setBowlerFetchError(null);
                  setShowAddBowlerInBackup(false);
                }}
                className="w-full px-4 py-2 bg-gray-600/50 hover:bg-gray-700/50 text-white rounded-lg transition duration-200"
              >
                {t('matchScoring.closeBowlerSelector')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Bowler to System — rendered on top of the backup selector */}
      {showAddBowlerInBackup && (
        <AddBowlerModalAdmin
          onClose={() => setShowAddBowlerInBackup(false)}
          onCreated={() => {
            setShowAddBowlerInBackup(false);
            // Refresh the bowler list so the newly created bowler appears
            fetchAvailableBowlers(bowlerSearchTerm);
          }}
        />
      )}
    </div>
  );
};

export default MatchScoring;
